import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useState } from 'react';
import { Loader2, TriangleAlert } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { Trash2 } from 'lucide-react';

import Error from '@/components/common/Error';
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@/components/ui/form';
import { Checkbox } from '@/components/ui/checkbox';
import { useDeleteLocation, useLocations } from '@/data/location';

const formSchema = z.object({
  is_confirmed: z.boolean().refine((val) => val === true, {
    message: 'You must confirm this action by checking the box.'
  })
});

const DeleteLocationDialog = ({ location }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const deleteLocation = useDeleteLocation();
  const locations = useLocations();
  const navigate = useNavigate();

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      is_confirmed: false
    }
  });

  const onSubmit = async () => {
    setApiError('');
    setIsSubmitting(true);
    try {
      await deleteLocation(location.id);
      locations.mutate(
        (currentLocations) =>
          currentLocations.filter((loc) => loc.id !== location.id),
        false
      );
      setIsOpen(false);
      navigate('/location');
    } catch (err) {
      setIsSubmitting(false);
      setApiError(err.message);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="outline">
          <Trash2 />
          &nbsp;Delete Location
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete {location.name}</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <Alert variant="destructive">
              <TriangleAlert className="h-4 w-4" />
              <AlertTitle>Warning</AlertTitle>
              <AlertDescription>
                Deleting this location is permanent and cannot be undone.
              </AlertDescription>
            </Alert>
            <FormField
              control={form.control}
              name="is_confirmed"
              render={({ field }) => (
                <FormItem className="">
                  <div className="flex items-center gap-2 space-y-0 ">
                    <FormControl className="flex items-center ">
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <FormLabel>I want to delete this location</FormLabel>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
            {apiError && <Error message={apiError} />}
            <DialogFooter>
              <Button
                type="submit"
                disabled={isSubmitting}
                variant="destructive"
              >
                {isSubmitting && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Delete Location
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default DeleteLocationDialog;
